import { SIGN_ERROR_CODES } from '@/constants/sign';
import { DocumentService } from '@/services/DocumentService';
import { SigningErrorResponse } from '@/types/sign';
import { UseMutateFunction } from '@tanstack/react-query';
import { useToastNotification } from '@treyd-io/treyd-ui/hooks/useToastNotification';
import { AxiosError, AxiosResponse } from 'axios';
import { includes } from 'lodash';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useBankId = <TPayload, TError extends SigningErrorResponse>({
  signFn,
  cancelFn,
}: {
  signFn: UseMutateFunction<
    AxiosResponse,
    AxiosError<TError>,
    TPayload,
    unknown
  >;
  cancelFn: UseMutateFunction<
    AxiosResponse,
    AxiosError<unknown>,
    // biome-ignore lint/suspicious/noExplicitAny:
    any,
    unknown
  >;
}) => {
  const notification = useToastNotification();
  const { t } = useTranslation();
  const documentService = new DocumentService({ t });
  const [token, setToken] = useState('');
  const scriveUrl = useRef<string | undefined>();

  const onError = (error: AxiosError<TError>) => {
    const { error_code: errorCode, url, token } = error?.response?.data || {};

    const isErrorCodeHandled =
      errorCode &&
      includes(
        [
          SIGN_ERROR_CODES.PERSONAL_NUMBER_ERROR,
          SIGN_ERROR_CODES.VERIFICATION_METHOD_ERROR,
        ],
        errorCode
      );

    if (isErrorCodeHandled) {
      notification({
        title: documentService.signingErrorMessagesMapper[errorCode],
        type: 'error',
      });
      return;
    }
    if (token) {
      setToken(token);
      if (scriveUrl.current !== url) {
        scriveUrl.current = url;
      }
      return;
    }
    if (url) {
      window.location.replace(url);
      return;
    }
  };

  const onCancel = (payload: TPayload) => {
    cancelFn(undefined, {
      onSettled: () => {
        signFn(payload, {
          onError,
        });
      },
    });
  };

  const onSign = (payload: TPayload) => {
    signFn(payload, {
      onError,
    });
  };

  return {
    onCancel,
    onSign,
    token,
    scriveUrl,
  };
};
